/**
 * core-model — единственная реализация правил, общая для телефона и сервера.
 *
 * Всё, что здесь лежит, обязано давать одинаковый ответ офлайн на устройстве
 * и на сервере: цена за карат, приоритет источников, формат идентификаторов.
 * Расхождение между этими двумя ответами — самый дорогой класс ошибок в проекте,
 * поэтому кода в двух экземплярах здесь быть не должно.
 */

// ── источники и доверие ─────────────────────────────────────────────────────

export const SOURCE_TYPES = [
  'certificate',
  'gemologist',
  'supplier_claim',
  'certificate_ocr',
  'ai_extraction',
  'inferred_from_previous',
] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

export type Confidence = 'certified' | 'assessed' | 'claimed' | 'low';

/** Тот же порядок, что в SQL-функции source_rank. Менять только вместе. */
const SOURCE_RANK: Record<SourceType, number> = {
  certificate: 100,
  gemologist: 80,
  supplier_claim: 60,
  certificate_ocr: 50,
  ai_extraction: 30,
  inferred_from_previous: 10,
};

export function sourceRank(src: string): number {
  return SOURCE_RANK[src as SourceType] ?? 0;
}

export const CONFIDENCE_BY_SOURCE: Record<SourceType, Confidence> = {
  certificate: 'certified',
  gemologist: 'assessed',
  supplier_claim: 'claimed',
  certificate_ocr: 'claimed',
  ai_extraction: 'low',
  inferred_from_previous: 'low',
};

export interface Fact {
  id: string;
  subjectType: 'stone' | 'parcel' | 'supplier';
  subjectId: string;
  attribute: string;
  valueText?: string | null;
  valueNum?: number | null;
  unit?: string | null;
  sourceType: SourceType;
  confidence: Confidence;
  sourceRef?: Record<string, unknown> | null;
  needsReview: boolean;
  recordedAt: string;
  recordedBy?: string | null;
  supersededBy?: string | null;
}

/**
 * Победивший факт по каждому атрибуту — ровно то же правило, что во вью
 * stone_current. Источник важнее свежести: сертификат недельной давности бьёт
 * сегодняшнюю догадку модели.
 */
export function resolveCurrent(facts: Fact[]): Map<string, Fact> {
  const out = new Map<string, Fact>();
  for (const f of facts) {
    if (f.supersededBy) continue;
    const prev = out.get(f.attribute);
    if (!prev) {
      out.set(f.attribute, f);
      continue;
    }
    const better =
      sourceRank(f.sourceType) > sourceRank(prev.sourceType) ||
      (sourceRank(f.sourceType) === sourceRank(prev.sourceType) &&
        f.recordedAt > prev.recordedAt);
    if (better) out.set(f.attribute, f);
  }
  return out;
}

// ── деньги ──────────────────────────────────────────────────────────────────

/**
 * Деньги хранятся целым числом минорных единиц. Плавающая точка в цене камня
 * недопустима: 4800.00 после нескольких пересчётов становится 4799.999…,
 * и это попадает в коммерческое предложение.
 */
export interface Money {
  minor: number;
  currency: string;
}

const MINOR_DIGITS: Record<string, number> = { JPY: 0, KRW: 0, VND: 0 };

/**
 * Валюта из сказанного вслух.
 *
 * Модель пишет код (USD, THB), но в уже накопленных записях лежит то самое
 * слово, которое прозвучало: «долларов», «бат», «юаней». Читать старое надо
 * так же уверенно, как новое, иначе цена в батах молча покажется долларами —
 * ошибка в тридцать пять раз.
 *
 * Чего здесь нет намеренно: курсов. Пересчёт по выдуманному курсу хуже
 * честного «это баты»: на выставке торгуются в той валюте, что назвали.
 */
const CURRENCY_WORDS: Array<[RegExp, string]> = [
  [/^(usd|\$|доллар|долар|бакс|dollar|buck)/i, 'USD'],
  [/^(thb|฿|бат|baht)/i, 'THB'],
  [/^(eur|€|евро|euro)/i, 'EUR'],
  [/^(cny|rmb|юан|юань|yuan|renminbi)/i, 'CNY'],
  [/^(hkd|гонконг|hong ?kong)/i, 'HKD'],
  [/^(inr|₹|рупи|rupee)/i, 'INR'],
  [/^(ils|₪|шекел|shekel|shkalim)/i, 'ILS'],
  [/^(jpy|иен|йен|yen)/i, 'JPY'],
  [/^(sgd|сингапур|singapore)/i, 'SGD'],
  [/^(lkr|ланкийск)/i, 'LKR'],
];

/** Слово или код → код валюты. Непонятное — null, догадка тут дороже пустоты. */
export function normalizeCurrency(input: string | null | undefined): string | null {
  const t = (input ?? '').trim();
  if (!t) return null;
  for (const [re, code] of CURRENCY_WORDS) if (re.test(t)) return code;
  // Незнакомый трёхбуквенный код принимаем как есть: список валют мира
  // длиннее, чем список выставок, на которых мы бывали.
  return /^[A-Za-z]{3}$/.test(t) ? t.toUpperCase() : null;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', THB: '฿', CNY: '¥', JPY: '¥',
  INR: '₹', ILS: '₪', HKD: 'HK$', SGD: 'S$',
};

export function minorDigits(currency: string): number {
  return MINOR_DIGITS[currency] ?? 2;
}

export function parseMoney(input: string | number, currency = 'USD'): Money | null {
  const n = typeof input === 'number' ? input : Number(String(input).replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n)) return null;
  return { minor: Math.round(n * 10 ** minorDigits(currency)), currency };
}

export function formatMoney(m: Money): string {
  const d = minorDigits(m.currency);
  const major = m.minor / 10 ** d;
  const sym = CURRENCY_SYMBOL[m.currency] ?? '';
  const body = major.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });
  return sym ? `${sym}${body}` : `${body} ${m.currency}`;
}

/**
 * Цена за карат. Считает код, никогда не модель.
 * Округление до минорной единицы — вниз по правилу «половина к чётному»
 * не применяется намеренно: в торге принято обычное округление.
 */
export function pricePerCarat(total: Money, weightCt: number): Money | null {
  if (!(weightCt > 0)) return null;
  // Округляем до целой единицы валюты: в торге говорят «две тысячи за карат»,
  // а не «две тысячи семьдесят семь и девяносто два». Копейки в этом числе —
  // не точность, а шум: оно всё равно производное от округлённых веса и цены.
  const unit = 10 ** minorDigits(total.currency);
  const perCt = total.minor / weightCt;
  return { minor: Math.round(perCt / unit) * unit, currency: total.currency };
}

// ── идентификаторы ──────────────────────────────────────────────────────────

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * ULID: истинный первичный ключ, генерится на устройстве офлайн.
 * Сортируется по времени создания, поэтому годится и как порядок съёмки.
 */
export function ulid(now = Date.now()): string {
  let ts = '';
  let t = now;
  for (let i = 0; i < 10; i++) {
    ts = CROCKFORD[t % 32] + ts;
    t = Math.floor(t / 32);
  }
  const bytes = new Uint8Array(16);
  (globalThis.crypto ?? require('node:crypto').webcrypto).getRandomValues(bytes);
  let rand = '';
  for (let i = 0; i < 16; i++) rand += CROCKFORD[bytes[i] % 32];
  return ts + rand;
}

/** Временный номер до синхронизации: код устройства не даёт двум людям выдать один номер. */
export function localNo(exhibitionCode: string, supplierCode: string, deviceCode: string, seq: number): string {
  return `${exhibitionCode}-${supplierCode}-${deviceCode}-${String(seq).padStart(4, '0')}`;
}

/** Постоянный номер — присваивает только сервер, когда запись доехала. */
export function publicNo(exhibitionCode: string, supplierCode: string, seq: number): string {
  return `${exhibitionCode}-${supplierCode}-${String(seq).padStart(4, '0')}`;
}

/**
 * Короткое обозначение выставки для номеров камней. Выводится из названия,
 * а не спрашивается: «Hong Kong Jewellery Show 2026» → «HK26». Человеку не за
 * чем придумывать код, ему достаточно увидеть, что получилось.
 */
export function exhibitionCode(name: string): string {
  const year = name.match(/\b(19|20)(\d{2})\b/)?.[2] ?? '';
  const skip = new Set(['show', 'fair', 'expo', 'international', 'jewellery', 'jewelry',
    'gem', 'gems', 'week', 'выставка', 'ярмарка', 'международная']);
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w && !/^\d+$/.test(w) && !skip.has(w.toLowerCase()));

  const lat = words.map(asciiUpper).filter(Boolean);
  let letters = lat.slice(0, 2).map((w) => w[0]).join('');
  if (letters.length < 2 && lat[0]) letters = lat[0].slice(0, 3);
  if (!letters) letters = 'SHOW';

  return (letters + year).slice(0, 6);
}

const RU_LAT: Record<string, string> = {
  А:'A',Б:'B',В:'V',Г:'G',Д:'D',Е:'E',Ё:'E',Ж:'ZH',З:'Z',И:'I',Й:'Y',К:'K',Л:'L',
  М:'M',Н:'N',О:'O',П:'P',Р:'R',С:'S',Т:'T',У:'U',Ф:'F',Х:'H',Ц:'TS',Ч:'CH',
  Ш:'SH',Щ:'SCH',Ъ:'',Ы:'Y',Ь:'',Э:'E',Ю:'YU',Я:'YA',
};

/**
 * Приводим к латинице и цифрам. Номер камня диктуют по телефону, пишут
 * маркером на пакетике и вставляют в ссылку — иероглифам и кириллице там
 * не место. Кириллица переводится, остальное отбрасывается.
 */
function asciiUpper(s: string): string {
  return s
    .toUpperCase()
    .split('')
    .map((ch) => RU_LAT[ch] ?? ch)
    .join('')
    .replace(/[^A-Z0-9]/g, '');
}

/**
 * Код поставщика для номера камня.
 *
 * Режем по границе слова, а не по десятому символу: «Liming Gems Co., Ltd»
 * должно дать LIMING, а не LIMINGGE. Номер читают вслух и переписывают
 * от руки — обрубок посреди слова в этом мешает.
 */
/**
 * Место, откуда начинается день.
 *
 * Сначала это была только выставка: стенды, залы, номер дня. В Гонконге
 * покупатель ходит не по выставке, а по офисным зданиям — и о здании
 * известно то, что известно: иногда название, иногда только адрес, а иногда
 * лишь ориентир («напротив нефритового рынка»). Любого из трёх достаточно,
 * чтобы завести место и снимать; остальное подтянется с визитки.
 */
export type PlaceKind = 'show' | 'building';

export interface PlaceLike {
  kind?: PlaceKind | null;
  name?: string | null;
  address?: string | null;
  landmark?: string | null;
}

/** Чем место называют вслух: название, иначе адрес, иначе ориентир. */
export function placeTitle(p: PlaceLike): string {
  return (p.name || p.address || p.landmark || '').trim();
}

const BUILDING_NOISE = new Set(['building', 'bldg', 'tower', 'centre', 'center', 'plaza', 'house',
  'mansion', 'mansions', 'commercial', 'industrial', 'road', 'rd', 'street', 'st', 'avenue', 'ave',
  'lane', 'floor', 'unit', 'room', 'block', 'the', 'здание', 'башня', 'центр', 'улица', 'дом',
  'напротив', 'рядом', 'около', 'возле']);

/**
 * Код здания для номеров камней: «Jade Building» → JB, «123 Nathan Road» →
 * NR. Год не нужен — здание не выставка, оно никуда не уедет. Из ориентира
 * тоже выводится: «напротив Jade Market» → JM.
 */
export function buildingCode(text: string): string {
  const words = text
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w && !/^\d+$/.test(w) && !BUILDING_NOISE.has(w.toLowerCase()));
  const initials = (list: string[]) => {
    const lat = list.map(asciiUpper).filter(Boolean);
    let letters = lat.slice(0, 2).map((w) => w[0]).join('');
    if (letters.length < 2 && lat[0]) letters = lat[0].slice(0, 3);
    return letters;
  };
  // «Tower A» после отсева служебных слов — одна буква; тогда берём всё как есть.
  let letters = initials(words);
  if (letters.length < 2) letters = initials(text.replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean));
  return (letters || 'BLDG').slice(0, 6);
}

/** Код места — по его виду. Выставка кодируется как прежде, чтобы старые номера не поплыли. */
export function placeCode(p: PlaceLike): string {
  if (p.kind === 'building') return buildingCode(placeTitle(p));
  return exhibitionCode(p.name ?? '');
}

export function supplierCode(companyName: string): string {
  const noise = new Set(['CO', 'LTD', 'INC', 'LLC', 'GMBH', 'SA', 'AG', 'PTE',
    'COMPANY', 'LIMITED', 'GEMS', 'GEM', 'JEWELLERY', 'JEWELRY', 'TRADING', 'THE']);

  const words = companyName
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(/\s+/)
    .map(asciiUpper)
    .filter(Boolean);

  const meaningful = words.filter((w) => !noise.has(w));
  const pool = meaningful.length ? meaningful : words;

  let code = '';
  for (const w of pool) {
    if (!code) { code = w.slice(0, 10); continue; }
    if (code.length + w.length <= 10) code += w; else break;
  }
  // Название целиком иероглифами — латиницы не осталось. Даём нейтральный код;
  // человек переименует поставщика, когда будет удобно.
  return code || 'SUPPLIER';
}

// ── телефон ─────────────────────────────────────────────────────────────────

/**
 * Приводим номер к одному виду, чтобы «054-910-4882», «+972 54 910 4882»
 * и «0549104882» были одним и тем же человеком.
 *
 * Правило общее для телефона и сервера. Иначе человек введёт номер так, как
 * привык его писать, и не войдёт, хотя администратор его завёл, — а понять,
 * почему, будет невозможно.
 *
 * Израильский код страны 972 сворачивается в ведущий ноль. Номера других
 * стран пока просто очищаются от разделителей: разберёмся, когда появятся.
 */
export function normalizePhone(input: string): string {
  let d = (input || '').replace(/\D/g, '');
  if (d.startsWith('00972')) d = d.slice(5);
  else if (d.startsWith('972')) d = d.slice(3);
  else if (d.startsWith('0')) d = d.slice(1);
  return d ? '0' + d : '';
}

/** Показываем в привычном виде: 054-910-4882. */
export function formatPhone(input: string): string {
  const d = normalizePhone(input);
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : d;
}

/** Похоже на номер: достаточно цифр, чтобы это вообще имело смысл проверять. */
export function looksLikePhone(input: string): boolean {
  const d = normalizePhone(input);
  return d.length >= 10 && d.length <= 16;
}

// ── язык ────────────────────────────────────────────────────────────────────

/**
 * Русское склонение по числу: 1 камень, 2 камня, 5 камней.
 * Без этого интерфейс всё время говорит «1 ждут», и приложение выглядит
 * так, будто его писали второпях.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last > 1 && last < 5) return few;
  if (last === 1) return one;
  return many;
}

// ── статусы ─────────────────────────────────────────────────────────────────

export const STATUSES = ['viewed', 'interesting', 'strong', 'priority', 'purchased', 'rejected'] as const;
export type Status = (typeof STATUSES)[number];

export const STATUS_LABEL: Record<Status, string> = {
  viewed: 'Просмотрен',
  interesting: 'Интересен',
  strong: 'Сильный кандидат',
  priority: 'Приоритет',
  purchased: 'Куплен',
  rejected: 'Не подходит',
};

// ── словарь атрибутов ───────────────────────────────────────────────────────

/**
 * Что модель имеет право заполнить из диктовки. Ключ — атрибут, значение —
 * как показывать. Перечень закрытый: поля, которого здесь нет, извлечение
 * не создаст, даже если модель его придумает.
 */
export const ATTRIBUTES = {
  stone_type: { label: 'Тип', kind: 'text' },
  natural_synthetic: { label: 'Природа', kind: 'text' },
  variety: { label: 'Разновидность', kind: 'text' },
  weight_ct: { label: 'Вес', kind: 'num', unit: 'ct' },
  // Для партии вес называют диапазоном — «от двух до четырёх карат».
  // Это не то же самое, что вес одного камня, и в одно поле не складывается.
  weight_min_ct: { label: 'Вес от', kind: 'num', unit: 'ct' },
  weight_max_ct: { label: 'Вес до', kind: 'num', unit: 'ct' },
  shape: { label: 'Огранка', kind: 'text' },
  cut: { label: 'Тип огранки', kind: 'text' },
  origin: { label: 'Происхождение', kind: 'text' },
  treatment: { label: 'Обработка', kind: 'text' },
  color: { label: 'Цвет', kind: 'text' },
  clarity: { label: 'Чистота', kind: 'text' },
  dimensions: { label: 'Размеры', kind: 'text' },
  lab: { label: 'Лаборатория', kind: 'text' },
  report_no: { label: 'Номер отчёта', kind: 'text' },
  asking_price: { label: 'Цена', kind: 'money' },
  // Цену часто называют за карат, особенно за партию. Спутать её с общей —
  // ошибка в разы, поэтому это отдельное поле, а не то же самое число.
  asking_price_per_ct: { label: 'Цена за карат', kind: 'money' },
  /*
   * Цена за карат, если брать лот целиком, не перебирая.
   *
   * У партии цены всегда две: «пятьсот за карат, если выбирать, и четыреста
   * двадцать, если не выбирать». Обе — за карат, и разница между ними и есть
   * предмет торга. Сложить их в одно поле нельзя, а класть вторую в общую
   * стоимость — врать в разы: «420 за карат лотом» превращалось в «партия
   * стоит 420».
   */
  asking_price_per_ct_lot: { label: 'Цена за карат лотом', kind: 'money' },
  currency: { label: 'Валюта', kind: 'text' },
  quantity: { label: 'Количество', kind: 'num' },
  supplier_sku: { label: 'SKU поставщика', kind: 'text' },
  note: { label: 'Заметка', kind: 'text' },
} as const;

export type AttributeKey = keyof typeof ATTRIBUTES;
export const ATTRIBUTE_KEYS = Object.keys(ATTRIBUTES) as AttributeKey[];

/** Порядок показа в карточке — от опознания камня к коммерции. */
export const ATTRIBUTE_ORDER: AttributeKey[] = [
  'stone_type', 'natural_synthetic', 'variety',
  'weight_ct', 'weight_min_ct', 'weight_max_ct', 'shape', 'cut',
  'origin', 'treatment', 'color', 'clarity', 'dimensions',
  'lab', 'report_no',
  'asking_price', 'asking_price_per_ct', 'asking_price_per_ct_lot',
  'quantity', 'supplier_sku', 'note',
];

// ── отбор камней ────────────────────────────────────────────────────────────
//
// Один движок на всё: и на кнопки фильтра, и на фразу «шпинели до пяти тысяч
// за карат». Модель не ищет по каталогу — она лишь переводит фразу в этот же
// объект Filter, а отбирает уже код. Иначе один и тот же вопрос давал бы
// разные ответы, и проверить их было бы нечем.

export interface Range { from?: number | null; to?: number | null }

export type SortKey = 'recent' | 'weight' | 'price' | 'perCt' | 'supplier';

export interface Filter {
  /** Свободный текст: номер, поставщик, стенд, слово из заметки. */
  q?: string;
  types?: string[];
  colors?: string[];
  origins?: string[];
  shapes?: string[];
  labs?: string[];
  statuses?: Status[];
  supplierIds?: string[];
  exhibitionIds?: string[];
  weightCt?: Range;
  /** Цена за камень и цена за карат — разные вопросы, не одно поле. */
  price?: Range;
  perCt?: Range;
  cert?: 'certified' | 'claimed' | 'none';
  treatment?: 'untreated' | 'treated';
  kind?: 'stone' | 'parcel';
  sort?: SortKey;
}

export const EMPTY_FILTER: Filter = {};

/** Всё, что нужно знать о камне, чтобы решить, подходит ли он. */
export interface StoneRow {
  id: string;
  kind: 'stone' | 'parcel';
  status: string;
  no: string;
  supplierId: string;
  supplierName?: string;
  booth?: string;
  /** Где искать: «стенд 3B-21» на выставке, «12 эт. · оф. 1205» в здании. */
  where?: string;
  /** Валюта места — если поставщик свою не назвал. */
  currencyDefault?: string;
  exhibitionId?: string;
  capturedAt: string;
  /** Победившие значения по атрибутам. */
  values: Map<string, { text?: string | null; num?: number | null; sourceType?: string; confidence?: string }>;
  note?: string;
  hasCert?: boolean;
  certVerified?: boolean;
}

const norm = (s: unknown) => String(s ?? '').toLowerCase().trim();

/** Подстрокой и в обе стороны: «сапфир» найдётся в «Natural Sapphire» и наоборот. */
function hits(value: string | null | undefined, needles: string[]): boolean {
  const v = norm(value);
  if (!v) return false;
  return needles.some((n) => {
    const x = norm(n);
    return Boolean(x) && (v.includes(x) || x.includes(v));
  });
}

/**
 * Необлагороженный камень.
 *
 * Лаборатории пишут это отрицанием — «no indications of heating», — и путать
 * такую формулировку с пустым полем нельзя: пустое поле значит, что никто не
 * смотрел, а отрицание есть утверждение.
 */
const NO_TREAT = ['no indication', 'no evidence', 'unheated', 'not heated', 'none', 'nte',
                  'без облагораж', 'негрет', 'негрен', 'не грет', 'нет следов'];

export function isUntreated(text: string | null | undefined): boolean {
  const v = norm(text);
  return Boolean(v) && NO_TREAT.some((m) => v.includes(m));
}

function inRange(value: number | null | undefined, r?: Range): boolean {
  if (!r || (r.from == null && r.to == null)) return true;
  if (value == null || !Number.isFinite(value)) return false;
  if (r.from != null && value < r.from) return false;
  if (r.to != null && value > r.to) return false;
  return true;
}

/** В какой валюте названы цены этого камня. Не сказано — доллары. */
export function rowCurrency(row: StoneRow): string {
  return normalizeCurrency(row.values.get('currency')?.text) ?? row.currencyDefault ?? 'USD';
}

/**
 * Какую валюту подразумевают, когда её не называют.
 *
 * В Гонконге цену без уточнения говорят в гонконгских долларах, в Бангкоке —
 * в батах, в Джайпуре — в рупиях. Выводится из города или страны места и
 * подставляется только тогда, когда поставщик валюту не назвал: сказанное
 * вслух всегда важнее умолчания.
 */
const CITY_CURRENCY: Array<[RegExp, string]> = [
  [/hong\s*kong|гонконг|kowloon|коулун|tsim sha tsui|\bhk\b/i, 'HKD'],
  [/thai|таиланд|bangkok|бангкок|chanthaburi|чантабури/i, 'THB'],
  [/china|китай|shenzhen|шэньчжэнь|guangzhou|гуанчжоу|shanghai|шанхай|wuzhou|учжоу/i, 'CNY'],
  [/india|индия|jaipur|джайпур|mumbai|мумба|surat|сурат/i, 'INR'],
  [/sri\s*lanka|шри|colombo|коломбо|ratnapura|ратнапур/i, 'LKR'],
  [/israel|израил|ramat gan|рамат/i, 'ILS'],
  [/japan|япон|tokyo|токио/i, 'JPY'],
  [/singapore|сингапур/i, 'SGD'],
  [/germany|герман|idar|france|франц|italy|итал|spain|испан|belgium|бельг|antwerp|антверпен/i, 'EUR'],
];
export function currencyForPlace(p: { city?: string | null; country?: string | null; address?: string | null }): string {
  const text = [p.city, p.country, p.address].filter(Boolean).join(' ');
  for (const [re, code] of CITY_CURRENCY) if (re.test(text)) return code;
  return 'USD';
}

/**
 * Цена за карат: названная поставщиком, иначе вычисленная из цены и веса.
 *
 * Порядок именно такой. Цена с выбором названа чаще и выше — по ней камень
 * и сравнивают. Цена лотом идёт следом: у партии она бывает единственной
 * названной, и тогда честнее показать её, чем прочерк. Вычисленная —
 * последняя: это наша арифметика, а не слова поставщика.
 */
export function rowPerCt(row: StoneRow): number | null {
  const quoted = row.values.get('asking_price_per_ct')?.num;
  if (quoted != null) return Number(quoted);
  const lot = row.values.get('asking_price_per_ct_lot')?.num;
  if (lot != null) return Number(lot);
  const total = row.values.get('asking_price')?.num;
  const w = row.values.get('weight_ct')?.num;
  if (total == null || !w) return null;
  return Number(total) / Number(w);
}

export function rowWeight(row: StoneRow): number | null {
  const w = row.values.get('weight_ct')?.num;
  if (w != null) return Number(w);
  // У партии вес — диапазон; для сравнения берём его середину и помним,
  // что это оценка, а не измерение.
  const a = row.values.get('weight_min_ct')?.num;
  const b = row.values.get('weight_max_ct')?.num;
  if (a != null && b != null) return (Number(a) + Number(b)) / 2;
  return a != null ? Number(a) : b != null ? Number(b) : null;
}

export function matchStone(row: StoneRow, f: Filter): boolean {
  const v = (a: string) => row.values.get(a)?.text ?? null;

  if (f.kind && row.kind !== f.kind) return false;
  if (f.statuses?.length && !f.statuses.includes(row.status as Status)) return false;
  if (f.supplierIds?.length && !f.supplierIds.includes(row.supplierId)) return false;
  if (f.exhibitionIds?.length && !f.exhibitionIds.includes(row.exhibitionId ?? '')) return false;

  if (f.types?.length && !hits(v('stone_type'), f.types) && !hits(v('variety'), f.types)) return false;
  if (f.colors?.length && !hits(v('color'), f.colors)) return false;
  if (f.origins?.length && !hits(v('origin'), f.origins)) return false;
  if (f.shapes?.length && !hits(v('shape'), f.shapes) && !hits(v('cut'), f.shapes)) return false;
  if (f.labs?.length && !hits(v('lab'), f.labs)) return false;

  if (!inRange(rowWeight(row), f.weightCt)) return false;
  if (!inRange(row.values.get('asking_price')?.num ?? null, f.price)) return false;
  if (!inRange(rowPerCt(row), f.perCt)) return false;

  if (f.cert === 'certified' && !row.certVerified) return false;
  if (f.cert === 'claimed' && !(row.hasCert && !row.certVerified)) return false;
  if (f.cert === 'none' && row.hasCert) return false;

  if (f.treatment === 'untreated' && !isUntreated(v('treatment'))) return false;
  if (f.treatment === 'treated') {
    const t = v('treatment');
    if (!t || isUntreated(t)) return false;
  }

  if (f.q) {
    const hay = [
      row.no, row.supplierName, row.booth, row.note,
      v('stone_type'), v('variety'), v('color'), v('origin'), v('report_no'),
      v('supplier_sku'), v('treatment'), v('shape'),
    ].map(norm).join(' ');
    const words = norm(f.q).split(/\s+/).filter(Boolean);
    if (!words.every((w) => hay.includes(w))) return false;
  }
  return true;
}

export function sortRows(rows: StoneRow[], key: SortKey = 'recent'): StoneRow[] {
  const out = [...rows];
  const num = (x: number | null) => (x == null ? Number.POSITIVE_INFINITY : x);
  switch (key) {
    case 'weight': out.sort((a, b) => num(rowWeight(b)) - num(rowWeight(a))); break;
    case 'price': out.sort((a, b) => num(a.values.get('asking_price')?.num ?? null) - num(b.values.get('asking_price')?.num ?? null)); break;
    case 'perCt': out.sort((a, b) => num(rowPerCt(a)) - num(rowPerCt(b))); break;
    case 'supplier': out.sort((a, b) => norm(a.supplierName).localeCompare(norm(b.supplierName)) || b.capturedAt.localeCompare(a.capturedAt)); break;
    default: out.sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));
  }
  return out;
}

export const SORT_LABEL: Record<SortKey, string> = {
  recent: 'Сначала свежие',
  weight: 'Крупные выше',
  price: 'Дешевле выше',
  perCt: 'Дешевле за карат',
  supplier: 'По поставщику',
};

export function filterIsEmpty(f: Filter): boolean {
  return !Object.entries(f).some(([k, v]) => {
    if (k === 'sort' || v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return (v as Range).from != null || (v as Range).to != null;
    return String(v).length > 0;
  });
}

/** Короткие подписи выбранного — чтобы человек видел, что именно он спросил. */
export function filterChips(f: Filter, names?: { supplier?: (id: string) => string | undefined }): string[] {
  const out: string[] = [];
  // Числа с разбивкой разрядов: «2 000–5 000», а не «2000–5000».
  const n = (v: number) => v.toLocaleString('ru-RU');
  const range = (r: Range | undefined, unit: string) => {
    if (!r || (r.from == null && r.to == null)) return null;
    if (r.from != null && r.to != null) return `${n(r.from)}–${n(r.to)} ${unit}`;
    return r.from != null ? `от ${n(r.from)} ${unit}` : `до ${n(r.to!)} ${unit}`;
  };
  if (f.q) out.push(`«${f.q}»`);
  for (const list of [f.types, f.colors, f.origins, f.shapes, f.labs]) {
    if (list?.length) out.push(list.join(', '));
  }
  const w = range(f.weightCt, 'ct'); if (w) out.push(w);
  const p = range(f.price, '$'); if (p) out.push(p);
  const pc = range(f.perCt, '$/ct'); if (pc) out.push(pc);
  if (f.cert === 'certified') out.push('с проверенным отчётом');
  if (f.cert === 'claimed') out.push('отчёт не проверен');
  if (f.cert === 'none') out.push('без отчёта');
  if (f.treatment === 'untreated') out.push('без облагораживания');
  if (f.treatment === 'treated') out.push('с обработкой');
  if (f.kind === 'parcel') out.push('партии');
  if (f.kind === 'stone') out.push('поштучно');
  if (f.statuses?.length) out.push(f.statuses.map((s) => STATUS_LABEL[s]).join(', '));
  if (f.supplierIds?.length) {
    out.push(f.supplierIds.map((id) => names?.supplier?.(id) ?? 'поставщик').join(', '));
  }
  return out;
}
